import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import "./Expense.scss";
import {
  BiEdit,
  BiTrash,
  BiCalendar,
  BiDollarSign,
  BiTag,
  BiFileText,
  BiUser,
  BiUserCheck,
  BiFile,
  BiTagAlt,
  BiDollar,
  BiCalendarAlt,
} from "react-icons/bi";
import { useLoading } from "../../context/LoadingContext";
import { useNavigate } from "react-router-dom";
import { deleteExpense, getExpenseByUser } from "../../services/api/ExpenseApi";
import { useSearchResults } from "../../context/SearchResultsContext";
import { WrittenFormat } from "../../components/WrittenFormat";
import { useExpenseSelection } from "../../context/ExpenseSelectionContext";

import { IoGrid } from "react-icons/io5";
import { FaTableList } from "react-icons/fa6";

import DeleteConfirmationModal from "../../components/DeleteConfirmationModal/DeleteConfirmationModal";
import { getBothCourtCasesDetailsByCourtRef } from "../../services/api/CasesApi";
import { formatText } from "../../components/TruncateParties";

export const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [viewMode, setViewMode] = useState("table");
  const [caseData, setCaseData] = useState(null);

  const { showLoading, hideLoading } = useLoading();
  const { searchResults, searchType } = useSearchResults();
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Add Expense Selection Context
  const {
    searchTerm: expenseSearchTerm,
    setSearchTerm: setExpenseSearchTerm,
    filters,
    setFilters,
    filteredExpenses,
    setFilteredExpenses,
    clearAllFilters: clearExpenseFilters,
    hasActiveFilters: hasExpenseFilters,
    pagination,
    setPagination,
    resetPagination,
    updatePaginationInfo,
    loadMore,
    buildQueryParams,
    setIsLoading,
    triggerExpenseRefresh,
  } = useExpenseSelection();

  // Backend pagination and filtering parameters
  const [currentPage, setCurrentPage] = useState(0);
  const [expensesPerPage, setExpensesPerPage] = useState(5);

  const searchInput = expenseSearchTerm;

useEffect(() => {
  console.log("Refetching due to pagination change:", {
    currentPage,
    expensesPerPage,
    contextPage: pagination.page,
  });

  const refetchOnPaginationChange = async () => {
    try {
      showLoading();
      const params = new URLSearchParams();
      params.set("page", currentPage.toString());
      params.set("limit", expensesPerPage.toString());

      // Add search term
      if (searchInput && searchInput.trim()) {
        params.set("search", searchInput.trim());
      }

      // FIXED: Add all filter parameters with proper empty string checking
      if (filters.type && filters.type.trim() !== "") {
        params.set("expenseType", filters.type.trim());
      }
      if (filters.startDate && filters.startDate.trim() !== "") {
        params.set("startDate", filters.startDate.trim());
      }
      if (filters.endDate && filters.endDate.trim() !== "") {
        params.set("endDate", filters.endDate.trim());
      }
      if (filters.minAmount && filters.minAmount.trim() !== "") {
        params.set("minAmount", filters.minAmount.trim());
      }
      if (filters.maxAmount && filters.maxAmount.trim() !== "") {
        params.set("maxAmount", filters.maxAmount.trim());
      }
      if (filters.resourceName && filters.resourceName.trim() !== "") {
        params.set("resourceName", filters.resourceName.trim());
      }
      if (filters.typeOfResource && filters.typeOfResource.trim() !== "") {
        params.set("typeOfResource", filters.typeOfResource.trim());
      }
      if (filters.courtRef && filters.courtRef.trim() !== "") {
        params.set("courtRef", filters.courtRef.trim());
      }

      console.log("Making API call with params:", params.toString());

      const response = await getExpenseByUser(`?${params.toString()}`);
      const data = await response.data;
      const expenseList = data.expenses || [];

      console.log("API returned page:", data.page, "Expected page:", currentPage);

      setFilteredExpenses(expenseList);

      if (data.expenseCount !== undefined) {
        updatePaginationInfo(
          data.expenseCount,
          data.expenseCount > (data.page + 1) * data.limit
        );
      }
    } catch (err) {
      console.error("Error in refetch:", err);
      setError(err.message || "Failed to fetch expenses.");
    } finally {
      hideLoading();
    }
  };

  refetchOnPaginationChange();
}, [currentPage, expensesPerPage, filters, searchInput]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getDate()} ${date.toLocaleString("default", {
      month: "short",
    })} ${date.getFullYear().toString().slice(-2)}`;
  };

  const handleEdit = (expense) => {
    navigate(`/home/expenses/edit/${expense._id}`, {
      state: { expenseData: expense },
    });
  };

  const handleDeleteClick = (expenseId) => {
    setExpenseToDelete(expenseId);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!expenseToDelete) return;

    try {
      showLoading();
      await deleteExpense(expenseToDelete);
      setExpenses((prev) => prev.filter((exp) => exp._id !== expenseToDelete));
      if (paginatedExpenses.length === 1 && currentPage > 0) {
        // If so, go back to the previous page. This will trigger the useEffect to refetch.
        setCurrentPage(currentPage - 1);
      } else {
        // Otherwise, just refresh the current page's data using the trigger.
        triggerExpenseRefresh();
      }

      if (searchType === "expenses" && searchResults.length > 0) {
        console.log("Expense deleted while showing search results");
      }
    } catch (err) {
      console.error("Error deleting expense:", err);
      setError("Failed to delete expense. Please try again.");
    } finally {
      hideLoading();
      setIsDeleteModalOpen(false);
      setExpenseToDelete(null);
    }
  };

  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
    setExpenseToDelete(null);
  };

  // Update the display logic to use context
  // const displayExpenses = useMemo(() => {
  //   // Use filteredExpenses from context for unified behavior
  //   console.log('Using filteredExpenses from context:', filteredExpenses?.length || 0);
  //   return filteredExpenses || [];
  // }, [filteredExpenses]);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  // Apply local UI filters on top of context-provided filtered expenses

  const displayExpenses = useMemo(() => {
    // Priority 1: If we have search results, use them
    if (searchResults.length > 0 && searchType === "expenses") {
      console.log("Using search results");
      return searchResults;
    }

    // Priority 2: Use filtered expenses from context
    console.log("Using filtered expenses from context");
    return filteredExpenses;
  }, [filteredExpenses, searchResults, searchType]);

  const paginatedExpenses = useMemo(() => {
    return displayExpenses; // No local filtering needed
  }, [displayExpenses]);

  // const shouldShowNoDataMessage = () => {
  //   const hasLocalFilters = Object.values(filters).some((val) => val);
  //   const hasContextFilters = hasExpenseFilters;

  //   if (hasLocalFilters && finalDisplayExpenses.length === 0) return true;
  //   if (hasContextFilters && displayExpenses.length === 0) return true;

  //   return displayExpenses.length === 0;
  // };

  // Update the getNoDataMessage function

  const shouldShowNoDataMessage = useMemo(() => {
    return displayExpenses.length === 0;
  }, [displayExpenses.length]);

  // const getNoDataMessage = () => {
  //   const hasLocalFilters = Object.values(filters).some((val) => val);
  //   const hasContextFilters = hasExpenseFilters;

  //   if (hasLocalFilters && finalDisplayExpenses.length === 0) {
  //     return "No expenses found matching the applied filters.";
  //   }

  //   if (hasContextFilters && displayExpenses.length === 0) {
  //     return "No expenses found matching your search and selections.";
  //   }

  //   return "No expenses found. Start by adding a new one!";
  // };

  const getNoDataMessage = useMemo(() => {
    if (hasExpenseFilters && displayExpenses.length === 0) {
      return "No expenses found matching your search and filters.";
    }

    if (searchType === "expenses" && displayExpenses.length === 0) {
      return "No expenses found matching your search.";
    }

    return "No expenses found. Start by adding a new one!";
  }, [hasExpenseFilters, displayExpenses.length, searchType]);
  
const handleFilterChange = useCallback(
  (field, value) => {
    console.log("Filter changed:", field, "from", filters[field], "to", value);
    setIsLoading(true);

    // Always update the filter, even if it's an empty string
    const newFilters = { ...filters, [field]: value || "" };
    console.log("New filters object:", newFilters);
    setFilters(newFilters);

    // Reset to first page when filters change
    setCurrentPage(0);
  },
  [filters, setFilters, setIsLoading]
);

 useEffect(() => {
  const fetchFilteredExpenses = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      params.set("page", currentPage.toString());
      params.set("limit", expensesPerPage.toString());

      // Add search term
      if (searchInput && searchInput.trim()) {
        params.set("search", searchInput.trim());
      }

      // FIXED: Add all filter parameters with proper empty string checking
      // Only add parameter if the filter value is not empty after trimming
      if (filters.type && filters.type.trim() !== "") {
        params.set("expenseType", filters.type.trim());
      }
      if (filters.startDate && filters.startDate.trim() !== "") {
        params.set("startDate", filters.startDate.trim());
      }
      if (filters.endDate && filters.endDate.trim() !== "") {
        params.set("endDate", filters.endDate.trim());
      }
      if (filters.minAmount && filters.minAmount.trim() !== "") {
        params.set("minAmount", filters.minAmount.trim());
      }
      if (filters.maxAmount && filters.maxAmount.trim() !== "") {
        params.set("maxAmount", filters.maxAmount.trim());
      }
      if (filters.resourceName && filters.resourceName.trim() !== "") {
        params.set("resourceName", filters.resourceName.trim());
      }
      if (filters.typeOfResource && filters.typeOfResource.trim() !== "") {
        params.set("typeOfResource", filters.typeOfResource.trim());
      }
      if (filters.courtRef && filters.courtRef.trim() !== "") {
        params.set("courtRef", filters.courtRef.trim());
      }

      console.log("Making API call with params:", params.toString());

      const response = await getExpenseByUser(`?${params.toString()}`);
      const data = await response.data;
      const expenseList = data.expenses || [];

      setFilteredExpenses(expenseList);

      if (data.expenseCount !== undefined) {
        updatePaginationInfo(
          data.expenseCount,
          data.expenseCount > (data.page + 1) * data.limit
        );
      }
    } catch (err) {
      console.error("Error fetching filtered expenses:", err);
      setError(err.message || "Failed to fetch expenses.");
    } finally {
      setIsLoading(false);
    }
  };

  // FIXED: Always make API call when filters change, regardless of whether they're empty
  // This ensures that clearing a filter will refetch all data
  console.log("Filters changed, refetching expenses:", filters);
  fetchFilteredExpenses();
}, [
  filters,
  currentPage,
  expensesPerPage,
  searchInput,
  setFilteredExpenses,
  updatePaginationInfo,
  setIsLoading,
]);

  const handleExpensesPerPageChange = useCallback(
    (e) => {
      const newLimit = Number(e.target.value);
      setExpensesPerPage(newLimit);
      setCurrentPage(0); // Reset to first page
      // Update context pagination as well
      setPagination((prev) => ({
        ...prev,
        limit: newLimit,
        page: 0,
      }));
    },
    [setPagination]
  );

  // const handlePageChange = useCallback((newPage) => {
  //   setCurrentPage(newPage);
  //   // Update context pagination as well
  //   setPagination(prev => ({
  //     ...prev,
  //     page: newPage
  //   }));
  // }, [setPagination]);

  const handlePageChange = useCallback((newPage) => {
    console.log("handlePageChange called with:", newPage);
    setCurrentPage(newPage);
    // Don't update context pagination here - let the refetch useEffect handle it
  }, []);

const clearFilters = useCallback(() => {
  console.log("Clearing all filters from Expenses");
  
  // Clear local filters with proper empty strings
  setFilters({
    type: "",
    startDate: "",
    endDate: "",
    minAmount: "",
    maxAmount: "",
    resourceName: "",
    typeOfResource: "",
    courtRef: "",
  });
  
  // Clear context filters
  clearExpenseFilters();
  setCurrentPage(0);
}, [clearExpenseFilters, setFilters]);
  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [filters]);

  useEffect(() => {
    const fetchCaseDetails = async (courtRef) => {
      if (!courtRef) return;
      setError(null);
      try {
        const response = await getBothCourtCasesDetailsByCourtRef(courtRef);
        setCaseData((prevData) => ({
          ...prevData,
          [courtRef]: response.data,
        }));
      } catch (err) {
        console.error("Error fetching case details:", err);
        setError(err.response?.data?.message || "Failed to fetch case details");
      } finally {
      }
    };

    paginatedExpenses.forEach((expense) => {
      if (expense.courtRef) {
        fetchCaseDetails(expense.courtRef);
      }
    });
  }, [paginatedExpenses]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".filter-dropdown-wrapper")) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filterButtonRef = useRef();
  const filterDropdownRef = useRef();
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [dropdownOverflow, setDropdownOverflow] = useState("auto");

  useEffect(() => {
    if (isFilterOpen && filterButtonRef.current) {
      const buttonRect = filterButtonRef.current.getBoundingClientRect();
      const dropdownWidth = 365;
      const dropdownMargin = 4;
      let leftPosition = buttonRect.left;
      if (leftPosition + dropdownWidth > window.innerWidth) {
        leftPosition = buttonRect.right - dropdownWidth;
      }
      if (leftPosition < 0) {
        leftPosition = 10;
      }
      setDropdownPosition({
        top: buttonRect.bottom + dropdownMargin,
        left: leftPosition,
        maxHeight: 300,
      });
    }

    const handleScroll = (e) => {
      if (!isFilterOpen) return;
      const dropdown = document.querySelector(".filter-dropdown-box");
      if (dropdown && e && e.target && dropdown.contains(e.target)) {
        return;
      }
      setIsFilterOpen(false);
    };

    if (isFilterOpen) {
      window.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isFilterOpen]);

  useEffect(() => {
    if (isFilterOpen && filterDropdownRef.current) {
      const el = filterDropdownRef.current;
      if (el.scrollHeight > el.clientHeight) {
        setDropdownOverflow("auto");
      } else {
        setDropdownOverflow("visible");
      }
    }
  }, [isFilterOpen, dropdownPosition]);

  return (
    <>
      <div className="exp-main-div-header">
        <div className="invoices-header">
          <div className="view-toggle-wrapper">
            <button
              className={`view-toggle-btn ${
                viewMode === "grid" ? "active" : ""
              }`}
              onClick={() => setViewMode("grid")}
            >
              <IoGrid />
            </button>
            <button
              className={`view-toggle-btn ${
                viewMode === "table" ? "active" : ""
              }`}
              onClick={() => setViewMode("table")}
            >
              <FaTableList />
            </button>

            {/* Pagination Controls */}

            <div className="exp-page-filter">
              <div className="common-pagination-settings">
                <label htmlFor="expensePerPageSelect">Expenses per page:</label>
                <select
                  id="expensePerPageSelect"
                  value={expensesPerPage}
                  onChange={handleExpensesPerPageChange}
                >
                  {[5, 10, 15, 20, 25, 50].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="filter-dropdown-wrapper">
            <button
              ref={filterButtonRef}
              className="filter-toggle-btn"
              onClick={() => setIsFilterOpen((prev) => !prev)}
            >
              {isFilterOpen ? "Hide Filters" : "Show Filters"}
            </button>

            {isFilterOpen &&
              dropdownPosition.top !== 0 &&
              dropdownPosition.left !== 0 && (
                <div
                  className="filter-dropdown-box"
                  ref={filterDropdownRef}
                  style={{
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    maxHeight: dropdownPosition.maxHeight
                      ? `${dropdownPosition.maxHeight}px`
                      : "500px",
                    overflowY: dropdownOverflow,
                    position: "fixed",
                  }}
                >
                  <div className="filter-group">
                    <label>Type</label>
                    <input
                      type="text"
                      placeholder="Expense type"
                      value={filters.type || ""} // Ensure controlled input with empty string fallback
                      onChange={
                        (e) => handleFilterChange("type", e.target.value) // This will now properly handle empty strings
                      }
                    />
                  </div>

                  <div className="filter-group">
                    <label>Type of Resource</label>
                    <input
                      type="text"
                      placeholder="Type of resource"
                      value={filters.typeOfResource || ""}
                      onChange={(e) =>
                        handleFilterChange("typeOfResource", e.target.value)
                      }
                    />
                  </div>

                  <div className="filter-group">
                    <label>Resource Name</label>
                    <input
                      type="text"
                      placeholder="e.g., Clerk"
                      value={filters.resourceName || ""}
                      onChange={(e) =>
                        handleFilterChange("resourceName", e.target.value)
                      }
                    />
                  </div>

                  <div className="filter-group">
                    <label>Court Reference</label>
                    <input
                      type="text"
                      placeholder="Court reference"
                      value={filters.courtRef || ""}
                      onChange={(e) =>
                        handleFilterChange("courtRef", e.target.value)
                      }
                    />
                  </div>

                  <div className="filter-group">
                    <label>Start Date</label>
                    <input
                      type="date"
                      value={filters.startDate || ""}
                      onChange={(e) =>
                        handleFilterChange("startDate", e.target.value)
                      }
                    />
                  </div>

                  <div className="filter-group">
                    <label>End Date</label>
                    <input
                      type="date"
                      value={filters.endDate || ""}
                      onChange={(e) =>
                        handleFilterChange("endDate", e.target.value)
                      }
                    />
                  </div>

                  <div className="filter-group">
                    <label>Min Amount</label>
                    <input
                      type="number"
                      placeholder="Minimum ₹"
                      value={filters.minAmount || ""}
                      onChange={(e) =>
                        handleFilterChange("minAmount", e.target.value)
                      }
                      className="no-spinner"
                    />
                  </div>

                  <div className="filter-group">
                    <label>Max Amount</label>
                    <input
                      type="number"
                      placeholder="Maximum ₹"
                      value={filters.maxAmount || ""}
                      onChange={(e) =>
                        handleFilterChange("maxAmount", e.target.value)
                      }
                      className="no-spinner"
                    />
                  </div>


                  {/* <div className="filter-group">
                    <label>Max Amount</label>
                    <input
                      type="number"
                      placeholder="Minimum ₹"
                      value={filters.minAmount}
                      onChange={(e) =>
                        handleFilterChange("minAmount", e.target.value)
                      }
                    />
                  </div> */}

                  <div className="filter-actions">
                    <button
                      className="clear-btn"
                      onClick={() => {
                        setFilters({
                          type: "",
                          startDate: "",
                          endDate: "",
                          minAmount: "",
                          maxAmount: "",
                          resourceName: "",
                          typeOfResource: "",
                          courtRef: "",
                        });
                        setCurrentPage(0);
                      }}
                    >
                      Clear
                    </button>
                    <button
                      className="hide-btn"
                      onClick={() => setIsFilterOpen(false)}
                    >
                      Hide
                    </button>
                  </div>
                </div>
              )}
          </div>
        </div>

        {/* Add applied filters display */}
        {/* {(Object.values(filters).some(val => val) || hasExpenseFilters) && (
          <div className="applied-filters">
            <strong>Applied Filters:</strong>
            {expenseSearchTerm && <span>Search: {expenseSearchTerm}</span>}
            {filters.type && <span>Type: {filters.type}</span>}
            {filters.resourceName && <span>Resource: {filters.resourceName}</span>}
            {filters.startDate && <span>From: {filters.startDate}</span>}
            {filters.endDate && <span>To: {filters.endDate}</span>}
            {filters.minAmount && <span>Min Amount: ₹{filters.minAmount}</span>}
            {filters.maxAmount && <span>Max Amount: ₹{filters.maxAmount}</span>}
          </div>
        )} */}
        {(Object.values(filters).some((val) => val) || hasExpenseFilters) && (
          <div className="applied-filters">
            <span className="filters-text">Applied Filters:</span>

            {/* Search term filter pill */}
            {expenseSearchTerm && expenseSearchTerm.trim() && (
              <span
                className="filter-tag"
                onClick={() => {
                  setExpenseSearchTerm("");
                  // Clear DetailSection's search input
                  if (window.clearDetailSectionSearch) {
                    window.clearDetailSectionSearch();
                  }
                }}
              >
                Search: {formatText(expenseSearchTerm)} ×
              </span>
            )}

            {/* Individual filter pills */}
            {filters.type && (
              <span
                className="filter-tag"
                onClick={() => setFilters({ ...filters, type: "" })}
              >
                Type: {formatText(filters.type)} ×
              </span>
            )}

            {filters.resourceName && (
              <span
                className="filter-tag"
                onClick={() => setFilters({ ...filters, resourceName: "" })}
              >
                Resource: {formatText(filters.resourceName)} ×
              </span>
            )}

            {filters.typeOfResource && (
              <span
                className="filter-tag"
                onClick={() => setFilters({ ...filters, typeOfResource: "" })}
              >
                Type of Resource: {formatText(filters.typeOfResource)} ×
              </span>
            )}

            {filters.courtRef && (
              <span
                className="filter-tag"
                onClick={() => setFilters({ ...filters, courtRef: "" })}
              >
                Court Ref: {formatText(filters.courtRef)} ×
              </span>
            )}

            {filters.startDate && (
              <span
                className="filter-tag"
                onClick={() => setFilters({ ...filters, startDate: "" })}
              >
                From: {formatText(filters.startDate)} ×
              </span>
            )}

            {filters.endDate && (
              <span
                className="filter-tag"
                onClick={() => setFilters({ ...filters, endDate: "" })}
              >
                To: {formatText(filters.endDate)} ×
              </span>
            )}

            {filters.minAmount && (
              <span
                className="filter-tag"
                onClick={() => setFilters({ ...filters, minAmount: "" })}
              >
                Min Amount: ₹{formatText(filters.minAmount)} ×
              </span>
            )}

            {filters.maxAmount && (
              <span
                className="filter-tag"
                onClick={() => setFilters({ ...filters, maxAmount: "" })}
              >
                Max Amount: ₹{formatText(filters.maxAmount)} ×
              </span>
            )}

            <span
              className="filter-tag clear-all-tag"
              onClick={() => {
                clearExpenseFilters();
                // Clear local filters
                setFilters({
                  type: "",
                  startDate: "",
                  endDate: "",
                  minAmount: "",
                  maxAmount: "",
                  resourceName: "",
                  typeOfResource: "",
                  courtRef: "",
                });
                // Clear DetailSection's search input
                if (window.clearDetailSectionSearch) {
                  window.clearDetailSectionSearch();
                }
              }}
            >
              Clear All
            </span>
          </div>
        )}
        <div className="expense-container">
          {shouldShowNoDataMessage && !error ? (
            <div className="no-expenses-container">
              <p className="no-expenses">{getNoDataMessage}</p>
            </div>
          ) : viewMode === "grid" ? (
            paginatedExpenses.map((expense) => (
              <div
                key={expense._id}
                className="expense-card-main"
                onClick={() =>
                  navigate(`/home/expenses/viewexpense/${expense._id}`, {
                    state: {
                      parties:
                        caseData?.[expense.courtRef]?.case?.parties || null,
                    },
                  })
                }
                style={{ cursor: "pointer" }}
              >
                <div className="expense-header">
                  <div className="exp-main-detail">
                    <h2 className="expense-title">
                      {formatText(expense.typeOfResource) || "Miscellaneous Expense"}
                    </h2>
                  </div>
                  <div className="expense-actions">
                    <button
                      className="action-button edit-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(expense);
                      }}
                      aria-label="Edit expense"
                    >
                      <BiEdit size={16} />
                    </button>
                    <button
                      className="action-button delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(expense._id);
                      }}
                      aria-label="Delete expense"
                    >
                      <BiTrash size={16} />
                    </button>
                  </div>
                </div>
                {expense.courtRef && (
                  <span
                    className="exp-courtRef-tag"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/home/cases/casedetails/${expense.courtRef}`);
                    }}
                  >
                    {caseData && caseData[expense.courtRef]?.case?.parties
                      ? WrittenFormat(caseData[expense.courtRef].case.parties)
                      : "Parties not available"}
                    - ({expense.courtRef})
                  </span>
                )}
                <div className="expense-details-grid">
                  <span className="exp-detail-item">
                    <BiCalendarAlt className="detail-icon" />
                    <span className="detail-label">Date:</span>{" "}
                    <span className="detail-value">
                      {formatDate(expense.date)}
                    </span>
                  </span>
                  <span className="exp-detail-item">
                    <BiDollar className="detail-icon" />
                    <span className="detail-label">Amount:</span>{" "}
                    <span className="detail-value amount">
                      ₹{expense.amount}
                    </span>
                  </span>
                  <span className="exp-detail-item">
                    <BiTagAlt className="detail-icon" />
                    <span className="detail-label">Type:</span>{" "}
                    <span className="detail-value expense-type-tag">
                      {formatText(expense.type)}
                    </span>
                  </span>
                  {expense.resourceName && (
                    <span className="exp-detail-item">
                      <BiUserCheck className="detail-icon" />
                      <span className="detail-label">Resource:</span>{" "}
                      <span className="detail-value">
                        {formatText(expense.resourceName)}
                      </span>
                    </span>
                  )}
                </div>

                {expense.description && (
                  <div className="expense-description">
                    <p>
                      <strong>Description:</strong> {expense.description}
                    </p>
                  </div>
                )}

                {expense.documentUrl && (
                  <div className="expense-document">
                    <a
                      href={expense.documentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Document
                    </a>
                  </div>
                )}
              </div>
            ))
          ) : (
            <table className="expenses-table">
              <thead>
                <tr>
                  <th>Type/Resource</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Type</th>
                  <th>Resource Name</th>
                  <th>Court Ref</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedExpenses.map((expense) => (
                  <tr
                    key={expense._id}
                    onClick={() =>
                      navigate(`/home/expenses/viewexpense/${expense._id}`, {
                        state: {
                          parties:
                            caseData?.[expense.courtRef]?.case?.parties || null,
                        },
                      })
                    }a
                    style={{ cursor: "pointer" }}
                  >
                    <td>{formatText(expense.typeOfResource) || "Miscellaneous"}</td>
                    <td>{formatDate(expense.date)}</td>
                    <td>₹{expense.amount}</td>
                    <td>{formatText(expense.type)}</td>
                    <td>{formatText(expense.resourceName) || "—"}</td>
                    <td
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/home/cases/casedetails/${expense.courtRef}`);
                      }}
                      title={
                        expense.courtRef &&
                        caseData?.[expense.courtRef]?.case?.parties
                          ? WrittenFormat(
                              caseData[expense.courtRef].case.parties
                            )
                          : expense.courtRef
                          ? "Parties not available"
                          : ""
                      }
                    >
                      {expense.courtRef || "—"}
                    </td>
                    <td>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(expense);
                        }}
                      >
                        Update
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(expense._id);
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {(pagination.totalExpenses > expensesPerPage || currentPage > 0) && (
          <div className="pagination-controls">
            <button
              disabled={currentPage === 0}
              onClick={() => handlePageChange(0)}
            >
              First
            </button>
            <button
              disabled={currentPage === 0}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              Previous
            </button>
            <span>
              Page {currentPage + 1} of{" "}
              {Math.ceil(pagination.totalExpenses / expensesPerPage)}
            </span>
            <button
              disabled={
                currentPage >=
                Math.ceil(pagination.totalExpenses / expensesPerPage) - 1
              }
              onClick={() => handlePageChange(currentPage + 1)}
            >
              Next
            </button>
            <button
              disabled={
                currentPage >=
                Math.ceil(pagination.totalExpenses / expensesPerPage) - 1
              }
              onClick={() =>
                handlePageChange(
                  Math.ceil(pagination.totalExpenses / expensesPerPage) - 1
                )
              }
            >
              Last
            </button>
          </div>
        )}
      </div>
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        itemType="this expense"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </>
  );
};
